import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { combineLatest, merge, Observable, of } from 'rxjs';
import { Action, select, Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { CartService } from '~shared/modules/cart/services/cart.service';
import { AddToCartErrorLog, AddToCartEvent } from '../../models';
import {
  AddToCart,
  AddToCartFailure,
  AddToCartSuccess,
  CartActionTypes,
  GetCartTotalQuantity,
  GetCartTotalQuantityFailure,
  GetCartTotalQuantitySuccess,
  ShowMiniCart,
} from './cart.actions';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { CartNotificationsService } from '~shared/modules/shopping-cart-global/services/cart-notifications.service';
import * as fromCartStore from '~shared/modules/shopping-cart-store';
import * as fromUserInfo from '~shared/modules/user-info/store';
import { ReadShoppingCartSuccess } from '~shared/modules/shopping-cart-store/store/shopping-cart.actions';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { CartUpdateApplicationEvent } from '~shared/modules/application-event/models/cart-update-application-event.model';
import { CartFeatureService } from '../../services/cart-feature.service';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { ProductTrackingService } from '~app/modules/product/services/product-tracking.service';
import { CartFailureLoggerService } from '~shared/modules/shopping-cart-global/services/cart-failure-logger.service';
import { PageDetectionUtil } from '~shared/utils/page-detection.util';
import { MediaObserver } from '@angular/flex-layout';
import * as fromRouter from '~core/store';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { CartProductHasVolumePriceEnum } from '~shared/modules/shopping-cart-global/constants/log-failure-string-constant.enum';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

@Injectable()
export class CartEffects {
  activeRoute$ = this.store.pipe(select(fromRouter.getRouterState));

  @Effect({ dispatch: false })
  onNavigate$: Observable<Action> = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    tap((_) => this.cartNotificationsSvc.removeAllNotifications())
  );

  @Effect()
  onCartUpdated$: Observable<Action> = this.actions$.pipe(
    ofType(
      fromCartStore.ShoppingCartActionTypes.UPDATE_QUANTITY_SUCCESS,
      fromCartStore.ShoppingCartActionTypes.DELETE_PRODUCT_SUCCESS,
      fromCartStore.ShoppingCartActionTypes.READ_PARTIAL_SHOPPING_CART,
      fromUserInfo.UserActionTypes.USER_LOGOUT
    ),
    map(() => new GetCartTotalQuantity()),
    catchError((error) => of(new GetCartTotalQuantityFailure({ error })))
  );

  @Effect({ dispatch: false })
  onCartAdd$: Observable<any> = combineLatest([this.actions$, this.cartFeatureService.isCartUpdateFeatureEnabled]).pipe(
    filter(([action, isEnabled]: [ReadShoppingCartSuccess, boolean]) => {
      return (
        isEnabled &&
        action.type === fromCartStore.ShoppingCartActionTypes.READ_SHOPPING_CART_SUCCESS &&
        [
          CartActionTypes.ADD_TO_CART_SUCCESS,
          fromCartStore.ShoppingCartActionTypes.UPDATE_QUANTITY_SUCCESS,
          fromCartStore.ShoppingCartActionTypes.DELETE_PRODUCT_SUCCESS,
        ].includes(action.actionType)
      );
    }),
    distinctUntilChanged(),
    tap(([action, _]: [ReadShoppingCartSuccess, boolean]) => {
      const payload = new CartUpdateApplicationEvent(action.payload.offers, false);
      this.applicationEventService.publish(payload);
    })
  );

  @Effect()
  onProductDeleted$: Observable<Action> = this.actions$.pipe(
    ofType(fromCartStore.ShoppingCartActionTypes.DELETE_PRODUCT_SUCCESS),
    map(() => new GetCartTotalQuantity()),
    catchError((error) => of(new GetCartTotalQuantityFailure({ error })))
  );

  @Effect()
  publishAddToCart$: Observable<Action> = this.actions$.pipe(
    ofType(CartActionTypes.ADD_TO_CART),
    map((action: AddToCart) => action.payload),
    switchMap((data: AddToCartEvent) => {
      return this.cartService
        .addProduct(data.offer, data.quantity, data.valueAddedServices, data.trackingData, data.accountType)
        .pipe(
          map(() => new AddToCartSuccess(data.accountType)),
          take(1),
          catchError((e: any) =>
            of(
              new AddToCartFailure({
                error: {
                  offerId: data.offer,
                  itemId: data.productId,
                  quantity: data.quantity,
                  offerPrice: data.trackingData.offerPrice,
                  hasVolumePrice: data.trackingData.volumePricingShown,
                  sellerName: data.trackingData.sellerName,
                  leafCategoryId: data.trackingData.leafCategoryId,
                  leafCategoryName: data.trackingData.leafCategoryName,
                  error: e,
                },
              })
            )
          )
        );
    })
  );

  @Effect()
  showSuccessNotification$: Observable<Action> = this.actions$.pipe(
    ofType(CartActionTypes.ADD_TO_CART_SUCCESS),
    tap(() => {
      this.cartNotificationsSvc.notifyCartSuccess();
    }),
    map(() => new GetCartTotalQuantity()),
    catchError((error) => of(new GetCartTotalQuantityFailure({ error })))
  );

  @Effect()
  showFailureNotification$: Observable<any> = combineLatest([this.actions$, this.identityService.isAuthorized$]).pipe(
    filter(([action, _]: [AddToCartFailure, boolean]) => {
      return action.type === CartActionTypes.ADD_TO_CART_FAILURE;
    }),
    distinctUntilChanged(),
    map(([action, isLoggedIn]) => [action.payload && action.payload.error, isLoggedIn]),
    tap(([error, isLoggedIn]: [AddToCartErrorLog, boolean]) => {
      const isGuest = !isLoggedIn;
      const market = getCountryDomainCode(this.webAppBuyerRequest)

      if (error) {
        this.productTrackingService.trackOfferPriceSyncIssue(error.itemId);
        this.cartNotificationsSvc.showNotEnoughItemsNotification();
      } else {
        this.cartNotificationsSvc.showErrorNotification();
      }

      this.cartFailureLoggerService
        .logCartFailure(
          error?.itemId,
          error?.offerId,
          isGuest,
          market,
          parseFloat(error?.offerPrice),
          error?.hasVolumePrice === CartProductHasVolumePriceEnum.Yes,
          error?.sellerName,
          error?.leafCategoryId,
          error?.leafCategoryName,
          error?.quantity,
          error
        )
        .pipe(take(1))
        .subscribe();
    }),
    map(() => new GetCartTotalQuantity()),
    catchError((error) => of(new GetCartTotalQuantityFailure({ error })))
  );

  @Effect()
  getCartTotalQuantity$: Observable<Action> = this.actions$.pipe(
    ofType(CartActionTypes.GET_CART_TOTAL_QUANTITY),
    switchMap(() =>
      this.cartService.getCartTotalQuantity().pipe(map((count) => new GetCartTotalQuantitySuccess({ count })))
    ),
    catchError((error) => of(new GetCartTotalQuantityFailure({ error })))
  );

  @Effect()
  getShoppingCartData$: Observable<Action> = this.actions$.pipe(
    ofType(CartActionTypes.ADD_TO_CART_SUCCESS),
    map((action: AddToCartSuccess) => {
      return new fromCartStore.ReadShoppingCart({
        actionType: action.type,
      });
    }),
    catchError((error) => of(new GetCartTotalQuantityFailure({ error })))
  );

  @Effect()
  showMiniCart$ = combineLatest([this.actions$, this.activeRoute$]).pipe(
    filter(([action, routeState]: [ReadShoppingCartSuccess, any]) => {
      return (
        action.type === fromCartStore.ShoppingCartActionTypes.READ_SHOPPING_CART_SUCCESS &&
        [
          CartActionTypes.ADD_TO_CART_SUCCESS,
          fromCartStore.ShoppingCartActionTypes.UPDATE_QUANTITY_SUCCESS,
          fromCartStore.ShoppingCartActionTypes.DELETE_PRODUCT_SUCCESS,
        ].includes(action.actionType) &&
        !PageDetectionUtil.isCheckoutPage(routeState.url) &&
        !this.checkIfMobileOrTablet()
      );
    }),
    map(() => new ShowMiniCart())
  );

  @Effect()
  updateCartDataOnRouteChange$ = merge(
    this.actions$.pipe(ofType(ROUTER_NAVIGATED)),
    this.identityService.isAuthorized$
  ).pipe(
    withLatestFrom(this.activeRoute$, this.identityService.isAuthorized$),
    filter(([_, routerState]) => {
      if (!(routerState && routerState.url)) {
        return false;
      }
      const pageUrl = routerState.url;
      return !PageDetectionUtil.isCheckoutPage(pageUrl) && !this.checkIfMobileOrTablet();
    }),
    map(() => {
      return new fromCartStore.ReadShoppingCart();
    })
  );

  private checkIfMobileOrTablet() {
    return this.mediaObserver.isActive(MEDIA_ALISES.mobile) || this.mediaObserver.isActive(MEDIA_ALISES.tablet);
  }

  constructor(
    private readonly actions$: Actions,
    private readonly cartService: CartService,
    private readonly cartNotificationsSvc: CartNotificationsService,
    private readonly cartFeatureService: CartFeatureService,
    private readonly applicationEventService: ApplicationEventService,
    private readonly productTrackingService: ProductTrackingService,
    private readonly cartFailureLoggerService: CartFailureLoggerService,
    private identityService: IdentityService,
    private store: Store<fromRouter.RouterState>,
    private mediaObserver: MediaObserver,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {}
}
